<script setup>
import { ref, reactive, computed } from "vue";
import { apiQuery } from "@/api/j24api";
// import { Auth } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";

const credentials = reactive({
  intunetenantid: "",
  intuneclientid: "",
  intunesecret: "",
});

const currentStep = ref(1);

var loading = ref(false);
const loadingButton = ref(false);
const showWarningDialog = ref(false);
const showdeploymentDialog = ref(false);
const showCompletionDialog = ref(false);
const showCompletionWarningDialog = ref(false);
const showFailedCredentialDialog = ref(false);
const showUnsupportedWarningDialog = ref(false);

const buttonColor = ref("darkgrey");
const buttonText = ref("Validate Credentials");
var search = ref("");

const expanded = ref([]);

var itemArray = ref([]);
var unsupportedArray = ref([]);

var selected = ref([]);
var selectedArray = ref([]);

var selectedScope = ref(["All"]);
var selectedType = ref(["All"]);

var exportedItemReport = [];

var scopeOptions = ref([
  "All",
  "Intune",
  "Windows",
  "macOS",
  "iOS-iPadOS",
  "Android",
  "Linux",
]);

var typeOptions = ref([
  "All",
  "AppCategories",
  "Applications",
  "AppProtectionPolicies",
  "AssignmentFilters",
  "DeploymentProfiles",
  "DeviceCompliance",
  "DeviceConfiguration",
  "DeviceEnrolmentConfigurations",
  "EndpointSecurity",
  "GroupPolicyConfiguration",
  "Scripts",
  "SettingsCategories",
]);

const steps = [
  { title: "Intune Credentials", value: 1, complete: false },
  { title: "Select Items", value: 2, complete: false },
  { title: "Review and Export", value: 3, complete: false },
];

const itemHeaders = [
  { title: "Name", value: "name", sortable: true, width: "60%" },
  { title: "Type", value: "type", sortable: true, width: "30%" },
  { title: "Scope", value: "scope", sortable: true, width: "10%" },
];

const collectionHeaders = [
  { title: "Name", value: "name", sortable: true },
  { title: "Description", value: "description", sortable: false },
];

const deployHeaders = [
  { title: "Name", value: "name", width: "60%" },
  { title: "Type", value: "type", width: "15%" },
  { title: "Scope", value: "scope", width: "10%" },
  { title: "Status", value: "status", width: "10%" },
  { title: "Remove", value: "actions" },
];

const itemsPerPage = [
  { value: 25, title: "25" },
  { value: 50, title: "50" },
  { value: 100, title: "100" },
  { value: -1, title: "All" },
];

async function getData() {
  const items = await apiQuery("ELA", "mdmTools/command", {
    mdm: "intune",
    command: "listExportableItems",
    creds: {
      intuneTenantId: credentials.intunetenantid,
      intuneClientId: credentials.intuneclientid,
      intuneSecret: credentials.intunesecret,
    },
  });

  items.forEach((item) => {
    /* Add a unique id to each item, this is to be the index for transitions between pages.
    unfortuantely this was the only way to acheive this as the Intune ID (intuneId) is not usable for this purpose. */
    item.id = Math.floor(Math.random() * 10000000000000000000);
    itemArray.value.push(item);
  });
}

async function verifyCredentialsButton(credentials) {
  loadingButton.value = true;

  const result = await apiQuery("ELA", "mdmTools/command", {
    mdm: "intune",
    command: "credentialsCheck",
    creds: {
      intuneTenantId: credentials.intunetenantid,
      intuneClientId: credentials.intuneclientid,
      intuneSecret: credentials.intunesecret,
    },
  });

  if (result == "Success") {
    // If the credentials are valid, get the references from MongoDB and move to the next step. Wait until collected.
    await getData();
    nextStep();
  } else {
    failedCredentialsDialogBox(true);
  }

  loadingButton.value = false;
}

function nextStep() {
  if (currentStep.value < steps.length) {
    // Mark the current step as complete.
    steps[currentStep.value - 1].complete = true;

    if (currentStep.value == 2 && getSelectedItemsDetails.value.length == 0) {
      // If the user has not selected any items, show the warning dialog box.
      selectionWarningDialogBox(true);
    } else {
      currentStep.value++;
    }
  }
}

function prevStep() {
  if (currentStep.value > 1) {
    currentStep.value--;
    // Mark the current step as incomplete if they go back.
    steps[currentStep.value - 1].complete = false;

    // If the user goes back to the first step, clear this array to prevent duplication.
    unsupportedArray.value = [];
  }
}

const getSelectedItemsDetails = computed(() => {
  const uniqueItemIds = new Set();
  return selected.value
    .map((id) => {
      /* When transitioning between steps, only the id of the selected items are passed,
      this function references the id and collects the rest of the missing data for the new page
      It also removes duplicate items just in case the user switches back and forth between the pages. */
      const selectedItem = itemArray.value.find((item) => item.id === id);
      if (selectedItem && !uniqueItemIds.has(id)) {
        uniqueItemIds.add(id);
        const { intuneId, name, description, scope, type } = selectedItem;
        // We add a new element to the object called status, which is used to track the status of the deployment.
        const newItem = {
          intuneId,
          name,
          description,
          scope,
          type,
          status: "Pending",
        };
        return newItem;
      }
    })
    .filter(Boolean);
});

const filteredItems = computed(() => {
  // If both selectedScope and selectedType are set to 'All', return all items.
  if (selectedScope.value[0] === "All" && selectedType.value[0] === "All") {
    return itemArray.value;
  }

  // Otherwise, filter based on selectedScope and selectedType.
  return itemArray.value.filter((item) => {
    const passesScope =
      selectedScope.value[0] === "All" ||
      selectedScope.value.includes(item.scope);
    const passesType =
      selectedType.value[0] === "All" || selectedType.value.includes(item.type);

    return passesScope && passesType;
  });
});

function getSelectedItems() {
  /* Moves the selected items from the getSelectedItemsDetails variable into the selectedArray variable.
  This is because we cannot process getSelectedItemsDetails directly. */

  selectedArray.value = getSelectedItemsDetails.value;
  return selectedArray.value;
}

function changeStatusColour(status) {
  switch (status) {
    case "Pending":
      return "grey";
    case "In Progress":
      return "orange";
    case "Complete":
      return "green";
    case "Error":
      return "red";
    case "Name conflict":
      return "blue";
    case "Unsupported":
      return "orange";
    default:
      return "grey";
  }
}

function selectionWarningDialogBox(status) {
  if (status == true) {
    showWarningDialog.value = true;
  } else {
    showWarningDialog.value = false;
  }
}

function unsupportedWarningDialogBox(status) {
  if (status == true) {
    showUnsupportedWarningDialog.value = true;
  } else {
    showUnsupportedWarningDialog.value = false;
  }
}

function deploymentDialogBox(status) {
  if (status == true) {
    showdeploymentDialog.value = true;
  } else {
    showdeploymentDialog.value = false;
  }
}

function completionDialogBox(status) {
  if (status == true) {
    showCompletionDialog.value = true;
  } else {
    showCompletionDialog.value = false;
  }
}

function completionWarningDialogBox(status) {
  if (status == true) {
    showCompletionWarningDialog.value = true;
  } else {
    showCompletionWarningDialog.value = false;
  }
}

function failedCredentialsDialogBox(status) {
  if (status == true) {
    showFailedCredentialDialog.value = true;
  } else {
    showFailedCredentialDialog.value = false;
  }
}

function deleteItem(item) {
  const index = selectedArray.value.indexOf(item);
  if (index > -1) {
    selectedArray.value.splice(index, 1);
  }
}

async function getPortalUser() {
  // Get the ELA authenticated user's email address.
  const currentAuthenticatedUser = await fetchAuthSession();
  if (currentAuthenticatedUser) {
    var email = currentAuthenticatedUser.tokens.idToken.payload.email;
  }

  return email;
}

async function exportSelectedItems() {
  showdeploymentDialog.value = false;

  var portalUser = await getPortalUser();

  for (const item of selectedArray.value) {
    if (item.status == "Complete" || item.status == "Skipping") {
      /* If the item has already been deployed in the same session, skip it.
      This is to prevent duplicate deployments if re-run (for example an error happened) */
      item.status = "Skipping";
    } else {
      item.status = "In Progress";

      // Insert the portal user's email address into the item object.
      item.latestEditor = portalUser;

      const result = await apiQuery("ELA", "mdmTools/command", {
        mdm: "intune",
        command: "export",
        creds: {
          intuneTenantId: credentials.intunetenantid,
          intuneClientId: credentials.intuneclientid,
          intuneSecret: credentials.intunesecret,
        },
        payload: item,
      });

      if (result == "Success") {
        // This updates the UI to show the item as complete.
        item.status = "Complete";
      } else if (result == "Name conflict") {
        // This updates the UI to show the item as a duplicate.
        item.status = result;
      } else if (result == "Error") {
        // This updates the UI to show the item as failed.
        item.status = "Error";
      } else {
        // This updates the UI to show the item as unsupported.
        item.status = "Unsupported";

        // Add the item to the unsupportedItems array for display later.
        var unsupportedItem = {
          name: item.name,
          reason: result,
        };
        unsupportedArray.value.push(unsupportedItem);
      }

      // Add the item to the exportedItemReport array including deployment the status.
      exportedItemReport.push({
        name: item.name,
        type: item.type,
        scope: item.scope,
        status: item.status,
      });
    }
  }

  // Send the deployment log to the ELA API.
  await sendLog();

  // If any items have a status of "Error", show the completion warning dialog box. Otherwise, show the completion dialog box.
  if (
    selectedArray.value.some(
      (item) => item.status === "Error" || item.status === "Name conflict"
    )
  ) {
    completionWarningDialogBox(true);
  } else {
    completionDialogBox(true);
  }

  if (selectedArray.value.some((item) => item.status === "Unsupported")) {
    unsupportedWarningDialogBox(true);
  }

  // Mark the final step as complete.
  const finalStepIndex = steps.findIndex((step) => step.value === 4);
  if (finalStepIndex !== -1) {
    steps[finalStepIndex].complete = true;
  }
}

async function sendLog() {
  var portalUser = await getPortalUser();

  const logObject = {
    mdm: "Intune",
    logType: "Export",
    intuneTenantId: credentials.intunetenantid,
    intuneClientId: credentials.intuneclientid,
    portalUser: portalUser,
    timestamp: new Date().toISOString(),
    log: exportedItemReport,
  };

  await apiQuery("ELA", "mdmTools/command", {
    mdm: "intune",
    command: "log",
    payload: logObject,
  });

  // Clear the exportedItemReport array.
  exportedItemReport = [];
}

async function refreshDataButton() {
  loading.value = true;

  // Clear data and selected items.
  itemArray.value = [];
  selected.value = [];

  // Get the latest data from Intune.
  await getData();

  loading.value = false;
}
</script>

<template>
  <v-stepper v-model="currentStep">
    <v-stepper-header>
      <v-stepper-item
        v-for="(step, index) in steps"
        :key="index"
        :title="step.title"
        :value="step.value"
        :subtitle="step.subtitle"
        :complete="step.complete"
      ></v-stepper-item>
    </v-stepper-header>
    <v-stepper-content
      v-for="(step, index) in steps"
      :key="index"
      :step="step.value"
    >
      <v-sheet v-if="step.value === currentStep">
        <v-card-text>
          <template v-if="step.value === 1">
            <v-card max-width="500" variant="elevated" class="mx-auto">
              <v-card-text>
                <v-table class="text-left">
                  <v-responsive class="mx-auto py-2" max-width="500">
                    <v-text-field
                      clearable
                      v-model="credentials.intunetenantid"
                      label="Tenant ID"
                      type="input"
                      hint="Your Azure tenant ID"
                      variant="outlined"
                    ></v-text-field>
                  </v-responsive>
                  <v-responsive class="mx-auto py-2" max-width="500">
                    <v-text-field
                      clearable
                      v-model="credentials.intuneclientid"
                      label="Client ID"
                      type="input"
                      hint="Your App Registrations client ID"
                      variant="outlined"
                    ></v-text-field>
                  </v-responsive>
                  <v-responsive class="mx-auto py-2" max-width="500">
                    <v-text-field
                      clearable
                      v-model="credentials.intunesecret"
                      label="Client Secret"
                      type="password"
                      hint="The App Registrations client secret used for authentication"
                      variant="outlined"
                    ></v-text-field>
                  </v-responsive>
                  <v-btn
                    block
                    class="text-none mb-4"
                    size="x-large"
                    @click="verifyCredentialsButton(credentials)"
                    :disabled="
                      !credentials.intunetenantid ||
                      !credentials.intuneclientid ||
                      !credentials.intunesecret ||
                      loadingButton
                    "
                    :loading="loadingButton"
                    variant="tonal"
                    v-bind:color="buttonColor"
                    v-bind:text="buttonText"
                  ></v-btn>
                </v-table>
              </v-card-text>
            </v-card>
            <v-spacer class="my-3"></v-spacer>
            <v-card
              color="rgb(247, 247, 247)"
              max-width="500"
              variant="elevated"
              class="mx-auto"
            >
              <v-card-title class="py-2 font-weight-black">Help</v-card-title>
              <v-card-text>
                For guidance on setting up the Azure Application Registration
                and required permissions, please follow the instructions by
                clicking the button below.
              </v-card-text>

              <v-card-text>
                <v-btn
                  block
                  class="text-none mb-4"
                  color="indigo-darken-3"
                  size="x-large"
                  variant="flat"
                  href="/App Registration Guide v1.2.pdf"
                  target="_blank"
                >
                  Download Guide
                </v-btn>
              </v-card-text>
            </v-card>
          </template>
          <template v-if="step.value === 2">
            <h2>Select configuration items to export</h2>
            <v-responsive>
              <div class="d-flex">
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Search"
                  variant="underlined"
                  class="input-field"
                  style="margin-right: 400px"
                ></v-text-field>
                <v-select
                  v-model="selectedType"
                  :items="typeOptions"
                  chips
                  multiple
                  label="Type"
                  variant="underlined"
                  class="input-field"
                  style="margin-right: 20px"
                ></v-select>
                <v-select
                  v-model="selectedScope"
                  :items="scopeOptions"
                  chips
                  multiple
                  label="Scope"
                  variant="underlined"
                  class="input-field"
                ></v-select>
              </div>
            </v-responsive>
            <v-data-table
              :headers="itemHeaders"
              :items="filteredItems"
              :search="search"
              item-key="id"
              show-select
              v-model="selected"
              class="elevation-2"
              show-expand
              :items-per-page="25"
              :items-per-page-options="itemsPerPage"
            >
              <template v-slot:expanded-row="{ columns, item }">
                <tr>
                  <td :colspan="columns.length">
                    <table class="expanded-row">
                      <thead>
                        <tr>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            {{
                              item.description
                                ? item.description
                                : "No Description"
                            }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </template>
          <template v-if="step.value === 3">
            <h2>Review your selected items before exporting</h2>
            <div style="margin-bottom: 10px"></div>
            <v-data-table
              :headers="deployHeaders"
              :items="getSelectedItems()"
              item-key="id"
              :items-per-page="-1"
              :items-per-page-options="[-1]"
              class="elevation-2"
            >
              <template #[`item.status`]="{ item }">
                <div style="display: flex; justify-content: left">
                  <v-chip :color="changeStatusColour(item.status)">{{
                    item.status
                  }}</v-chip>
                </div>
              </template>
              <template #[`item.actions`]="{ item }">
                <div style="display: flex; justify-content: center">
                  <v-icon @click="deleteItem(item)">mdi-delete</v-icon>
                </div>
              </template>
            </v-data-table>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-if="currentStep > 1"
            variant="elevated"
            @click="prevStep"
            class="mr-auto"
            color="rgb(247, 247, 247)"
            >Back</v-btn
          >
          <v-btn
            v-if="currentStep == 2"
            variant="elevated"
            @click="refreshDataButton"
            color="rgb(247, 247, 247)"
            >Refresh Data</v-btn
          >
          <v-btn
            v-if="currentStep > 1 && currentStep < 3"
            variant="elevated"
            @click="nextStep"
            color="primary"
            >Next</v-btn
          >
          <v-btn
            v-if="currentStep == 3"
            @click="deploymentDialogBox(true)"
            variant="elevated"
            color="primary"
            >Export Configuration</v-btn
          >
        </v-card-actions>
      </v-sheet>
      <v-dialog v-model="showWarningDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <v-card-text
              ><h3>Please select an item before proceeding</h3></v-card-text
            >

            <v-divider></v-divider>
            <v-card-actions>
              <v-btn
                class="flex-grow-1"
                variant="tonal"
                @click="selectionWarningDialogBox"
                >OK</v-btn
              ></v-card-actions
            >
          </v-card>
        </div>
      </v-dialog>
      <v-dialog v-model="showdeploymentDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <v-card-text
              ><h3>
                Please note that any custom customer specific data (e.g
                passwords) will be exported. Ensure profiles are clear of
                personalised data before proceeding.
              </h3>
              <br />
              <h3>
                You are about to export the selected items into the Jigsaw24
                database. Please do not close or refresh this page until
                complete. Are you sure you want to proceed?
              </h3>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-btn @click="deploymentDialogBox">Cancel</v-btn>
              <v-btn
                @click="exportSelectedItems"
                class="flex-grow-1"
                variant="tonal"
                >Proceed</v-btn
              >
            </v-card-actions>
          </v-card>
        </div>
      </v-dialog>
      <v-dialog v-model="showCompletionDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <div class="py-12 text-center">
              <v-icon
                class="mb-6"
                color="success"
                icon="mdi-check-circle-outline"
                size="128"
              ></v-icon>
              <div class="text-h4 font-weight-bold">Export Completed</div>
            </div>
            <v-divider></v-divider>
            <div class="pa-4 text-center">
              <v-btn
                color="medium-emphasis"
                min-width="200"
                variant="outlined"
                @click="completionDialogBox"
              >
                Close
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-dialog>
      <v-dialog
        v-model="showCompletionWarningDialog"
        max-width="500"
        persistent
      >
        <div class="text-center">
          <v-card>
            <div class="py-12 text-center">
              <v-icon
                class="mb-6"
                color="warning"
                icon="mdi-alert-circle-outline"
                size="128"
              ></v-icon>
              <div class="text-h4 font-weight-bold">Completed with Errors</div>
              <div style="padding-left: 20px; padding-right: 20px">
                <br />
                <div>
                  Some items have failed to export, please check the status of
                  the items before trying again. In the event of a 'Name
                  conflict', please rename the affected item in Intune and try
                  again.
                </div>
              </div>
            </div>
            <v-divider></v-divider>
            <div class="pa-4 text-center">
              <v-btn
                color="medium-emphasis"
                min-width="200"
                variant="outlined"
                @click="completionWarningDialogBox"
              >
                Close
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-dialog>
      <v-dialog
        v-model="showUnsupportedWarningDialog"
        max-width="800"
        persistent
      >
        <div class="text-center">
          <v-card>
            <div class="py-12 text-center">
              <div class="text-h4 font-weight-bold">Unsupported Items</div>
              <div style="padding-left: 20px; padding-right: 20px">
                <br />
                <div>
                  The following items are not supported for export. Please
                  remove them from the selection before trying again.
                </div>
                <br />
                <v-divider></v-divider>
                <table>
                  <thead>
                    <tr>
                      <th style="width: 200px">Name</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in unsupportedArray" :key="item.name">
                      <td style="width: 200px">{{ item.name }}</td>
                      <td>{{ item.reason }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <v-divider></v-divider>
            <div class="pa-4 text-center">
              <v-btn
                color="medium-emphasis"
                min-width="200"
                variant="outlined"
                @click="unsupportedWarningDialogBox"
              >
                Close
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-dialog>
      <v-dialog v-model="showFailedCredentialDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <div class="py-12 text-center">
              <v-icon
                class="mb-6"
                color="warning"
                icon="mdi-alert-circle-outline"
                size="128"
              ></v-icon>
              <div class="text-h4 font-weight-bold">
                Credential Check Failed
              </div>
              <div>
                Verify the credentials are entered correctly and try again
              </div>
            </div>
            <v-divider></v-divider>
            <div class="pa-4 text-center">
              <v-btn
                color="medium-emphasis"
                min-width="200"
                variant="outlined"
                @click="failedCredentialsDialogBox"
              >
                Close
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-dialog>
      <v-dialog v-model="loading" persistent width="auto">
        <v-card color="primary">
          <v-card-text>
            Refreshing data, please wait...
            <v-progress-linear indeterminate></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-stepper-content>
  </v-stepper>
</template>

<style scoped>
/* Required for the scope filter to work correctly. */
.input-field {
  width: 50%;
}
/* tbody tr:nth-of-type(odd) {
  background-color: rgb(247, 247, 247);
} */

/* tbody tr:nth-of-type(even) {
  background-color: ;
} */
.expanded-row {
  width: 100%;
  border-collapse: collapse;
}
.expanded-row th {
  background-color: rgb(210, 210, 210);
  border: 1px solid #a4a4a4;
}
.expanded-row td {
  padding: 1px;
  border: 1px solid #a4a4a4;
  /* background-color: rgb(247, 247, 247); */
}
/* Control the width of the columns in the expanded row. */
.expanded-row td:nth-child(1) {
  width: 20%;
}
.expanded-row td:nth-child(2) {
  width: 60%;
}
.expanded-row td:nth-child(3) {
  width: 10%;
  text-align: center;
}
.expanded-row td:nth-child(4) {
  width: 10%;
  text-align: center;
}
</style>
